import { MasonryComponent, TypedResponsiveMasonry } from '@/components/ResponsiveMasonryWrapper'
import { Stack, Text } from '@/components/custom-components'
import { FeatureTabs } from '@/components/custom-components/FeatureTabs/index.tsx'
import { InspirationRequestNotifier } from '@/components/custom-components/InspirationRequestNotifier'
import { Loader } from '@/components/custom-components/Loader'
import NotFoundBox from '@/components/custom-components/NotFound'
import { ErrorDisplay } from '@/components/error.tsx'
import { TemplateAdSearchParams } from '@/hooks/useFilterFeed.tsx'
import { trpc } from '@/utils/trpc.ts'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo } from 'react'
import { z } from 'zod'
import { orderFilter as OrderFilterType } from '../../../../src/shared/airtable'

import { DiscoverAdCard } from '@/components/templates/AdCard'
import useCardLayoutWithImpressions from '@/hooks/useCardLayoutWithImpressions'
import useUserPermissions from '@/hooks/useUserPermissions'
import { useInView } from 'react-intersection-observer'

export const Route = createFileRoute('/feeds/search/inspiration/ads')({
  component: All,
  validateSearch: (search: Record<string, unknown>): TemplateAdSearchParams => {
    const industry = typeof search?.industry === 'string' ? search.industry : undefined
    const formats = typeof search?.formats === 'string' ? search.formats : undefined
    const timeLive = typeof search?.timeLive === 'string' ? search.timeLive : undefined
    const statuses = typeof search?.statuses === 'string' ? search.statuses : undefined
    const brands = typeof search?.brands === 'string' ? search.brands : undefined
    const sideBarOpen = typeof search?.sideBarOpen === 'boolean' ? search.sideBarOpen : undefined
    const searchTerm: string | undefined = typeof search.searchTerm === 'string' ? search.searchTerm : undefined
    const orderFilter = search?.orderFilter as z.infer<typeof OrderFilterType> | undefined

    return {
      industry,
      formats,
      timeLive,
      statuses,
      sideBarOpen,
      orderFilter,
      brands,
      searchTerm,
    }
  },
})

function All() {
  const { searchTerm: querySearchTerm } = Route.useSearch()

  const { ref: scrollRef, inView } = useInView({
    threshold: 0,
    trackVisibility: true,
    delay: 100,
    initialInView: false,
  })

  const { userLimits } = useUserPermissions()

  const {
    data: inspirationFeed,
    isLoading,
    refetch,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = trpc.searchAdInspiration.useInfiniteQuery(
    {
      limit: 20,
      query: querySearchTerm ?? '',
    },
    {
      getNextPageParam: (lastPage) => lastPage.cursor,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      initialCursor: 1,
    },
  )
  useEffect(() => {
    refetch()
  }, [querySearchTerm])

  useEffect(() => {
    if (
      inView &&
      hasNextPage &&
      !isFetchingNextPage &&
      !isLoading &&
      inspirationFeed &&
      inspirationFeed.pages[inspirationFeed.pages.length - 1]?.data.length > 0
    ) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, fetchNextPage, inspirationFeed, isLoading, isFetchingNextPage])

  const ads = useMemo(() => {
    return (
      inspirationFeed?.pages.flatMap((page) => {
        return page.data.flat()
      }) ?? []
    )
  }, [inspirationFeed])

  if (isError) {
    return (
      <div className="px-10">
        <ErrorDisplay />
      </div>
    )
  }
  const { squareRef, desiredCardWidth, columns, gutterWidth } = useCardLayoutWithImpressions()

  return (
    <Stack className="gap-3 lg:gap-6">
      <Stack className="relative gap-3 lg:gap-8">
        <FeatureTabs
          defaultOrderValue={'Random'}
          tabItems={[
            {
              name: 'Ads',
              link: '/feeds/search/inspiration/ads',
              searchParams: { searchTerm: querySearchTerm },
            },
            {
              name: 'Emails',
              link: '/feeds/search/inspiration/emails',
              searchParams: { searchTerm: querySearchTerm },
            },
            {
              name: 'Landers',
              link: '/feeds/search/inspiration/landing-pages',
              searchParams: { searchTerm: querySearchTerm },
            },
          ]}
        />
        <Stack className="gap-3 lg:gap-6 relative">
          <div className={'flex justify-between gap-5 items-center flex-wrap xs:flex-nowrap'}>
            <Text weight="semibold" size={'xxl'} className="w-fit">
              Your Search Results{' '}
              {inspirationFeed?.pages[0]?.totalHits ? `(${inspirationFeed?.pages[0]?.totalHits})` : ''}:
              <span className="opacity-45">“{querySearchTerm}”</span>
            </Text>
          </div>
        </Stack>
      </Stack>

      {userLimits ? <InspirationRequestNotifier pageType="ad" limitLeft={userLimits.request.ad} /> : null}
      {isLoading ? (
        <div className="flex justify-center items-center w-full h-screen">
          <Loader />
        </div>
      ) : (
        <div>
          {ads.length === 0 ? (
            <NotFoundBox />
          ) : (
            <div ref={squareRef} className={'relative w-full lg:px-0 pb-10'}>
              {inspirationFeed && (
                <div>
                  <TypedResponsiveMasonry
                    columnsCountBreakPoints={columns ? { 0: columns } : {}} // Columns is determined by the width of the container
                  >
                    <MasonryComponent gutter={gutterWidth / 16 + 'rem'}>
                      {ads.map((ad) => (
                        <DiscoverAdCard
                          key={ad.id}
                          DesiredWidth={desiredCardWidth}
                          adData={{
                            id: ad.id,
                            reach: null,
                            earliestView: '',
                            adRunningDays: 0,
                            imageUrl: ad.imageUrl ?? null,
                            brandId: ad.brandId ?? null,
                            brandName: ad.brandName ?? null,
                            brandImage: ad.brandImage ?? null,
                            videoUrl: ad.videoUrl ?? null,
                            isSaved: ad.isSaved ?? false,
                          }}
                          IsPublic={false}
                          IsLocked={false}
                        />
                      ))}
                    </MasonryComponent>
                  </TypedResponsiveMasonry>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <div ref={scrollRef} className="flex justify-center w-full min-h-14 h-14">
        {isFetchingNextPage && <Loader />}
      </div>
    </Stack>
  )
}

export default All
